import LockResetIcon from '@mui/icons-material/LockReset'
import { Alert } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { sendPasswordResetEmail } from 'firebase/auth'
import * as React from 'react'

import PreLoginCard from '../components/common/PreLoginCard'
import PreLoginLayout from '../components/common/PreLoginLayout'
import initFirebase from '../db/initFirebase'

export default function ForgotPassword() {
  const [email, setEmail] = React.useState('')
  const [error, setError] = React.useState('')
  const [success, setSuccess] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setSuccess(false)

    if (!email.trim()) {
      setError('Please enter your email address.')
      return
    }

    setIsLoading(true)
    try {
      const auth = initFirebase.getFireBaseStore().auth
      await sendPasswordResetEmail(auth, email.trim())
      setSuccess(true)
    } catch (err: unknown) {
      const msg = String(err)
      if (msg.includes('user-not-found')) {
        // Don't reveal whether the email exists for security
        setSuccess(true)
      } else if (msg.includes('invalid-email')) {
        setError('Please enter a valid email address.')
      } else {
        setError('Something went wrong. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <PreLoginLayout>
      <PreLoginCard>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Avatar sx={{ bgcolor: '#2563eb', width: 40, height: 40 }}>
              <LockResetIcon />
            </Avatar>
            <Typography
              component='h1'
              variant='h5'
              sx={{ fontWeight: 700, color: '#1e293b' }}
            >
              Reset Password
            </Typography>
          </Box>

          {success ? (
            <>
              <Alert severity='success' sx={{ width: '100%', mb: 2 }}>
                If an account exists for {email}, a password reset link has been
                sent. Check your inbox.
              </Alert>
              <Link
                href='/signin'
                variant='body2'
                sx={{
                  color: '#2563eb',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Back to Sign In
              </Link>
            </>
          ) : (
            <>
              <Typography
                variant='body2'
                sx={{ color: '#64748b', mb: 2, textAlign: 'center' }}
              >
                Enter your email address and we&apos;ll send you a link to reset
                your password.
              </Typography>

              <Box
                component='form'
                onSubmit={handleSubmit}
                noValidate
                sx={{ width: '100%' }}
              >
                <TextField
                  margin='normal'
                  required
                  fullWidth
                  id='email'
                  label='Email Address'
                  name='email'
                  autoComplete='email'
                  autoFocus
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff !important'
                    },
                    '& .MuiInputLabel-root': { color: '#64748b' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' },
                    '& .MuiOutlinedInput-input': { color: '#1e293b' },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    }
                  }}
                />
                <Button
                  type='submit'
                  fullWidth
                  variant='contained'
                  disabled={isLoading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    fontWeight: 600,
                    padding: '12px',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#1e40af' },
                    '&:disabled': { backgroundColor: '#9ca3af' }
                  }}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                {error && (
                  <Alert
                    severity='error'
                    onClose={() => setError('')}
                    sx={{ mb: 2 }}
                  >
                    {error}
                  </Alert>
                )}
                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    href='/signin'
                    variant='body2'
                    sx={{
                      color: '#2563eb',
                      fontWeight: 500,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Back to Sign In
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </PreLoginCard>
    </PreLoginLayout>
  )
}
